import type { ReactElement } from "react";
import { Datagrid, FunctionField, TextField, useListContext } from "react-admin";
import { useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { useNavigate } from "react-router";
import { grants } from "../../backoffice.access_control";
import { examLocationWeekdays, type ExamLocation } from "../../model/ExamLocation";
import { examTypes } from "../../model/ExamType";
import { renderAddress } from "../../misc/AddressDisplay";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { ExamLocationMobileListItem } from "./ExamLocationMobileListItem";

export function ExamLocationsDataGrid(props: { empty?: ReactElement }) {
  const { empty } = props;
  const { isLoading, data, total } = useListContext<ExamLocation>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const rowClick = grants.has("ExamLocation:edit") ? "edit" : false;

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty ?? null;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((examLocation) => (
          <ExamLocationMobileListItem
            key={examLocation.id}
            examLocation={examLocation}
            onClick={rowClick === false ? undefined : () => navigate(`/examLocations/${examLocation.id}`)}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid rowClick={rowClick} bulkActionButtons={false} empty={empty}>
      <TextField label="Name" source="name" />
      <FunctionField
        label="Adresse"
        source="postalAddress"
        render={(examLocation: ExamLocation) =>
          renderAddress(examLocation?.postalAddress ?? undefined, { oneLine: true })
        }
      />
      <FunctionField
        label="Prüfungsarten"
        render={(record: ExamLocation) =>
          Array.isArray(record.types) ? record.types.map(_examTypeMapper).join(" ") : ""
        }
      />
      <TextField label="praktische Prüfungstage im Voraus" source="practicalExamDaysInAdvance" />
      <FunctionField
        label="praktische Prüfung Wochentags"
        render={(record: ExamLocation) =>
          Array.isArray(record.practicalExamWeekdays)
            ? record.practicalExamWeekdays.map(_examLocationWeekdaysMapper).join(", ")
            : ""
        }
      />
      <TextField label="Theorieprüfung Tage im Voraus" source="theoryExamDaysInAdvance" />
      <FunctionField
        label="Theorieprüfung Wochentags"
        render={(record: ExamLocation) =>
          Array.isArray(record.theoryExamWeekdays)
            ? record.theoryExamWeekdays.map(_examLocationWeekdaysMapper).join(", ")
            : ""
        }
      />
    </Datagrid>
  );
}

const _examLocationWeekdaysMapper = (weekday: keyof typeof examLocationWeekdays) => {
  return examLocationWeekdays[weekday] || weekday;
};

const _examTypeMapper = (examType: keyof typeof examTypes) => {
  return examTypes[examType] || examType;
};
